import { SetStateAction, Dispatch, ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react'
import { Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useSearchParams } from 'react-router-dom'
import { EventSnippet } from '../../interfaces/IEvent'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import {
  AffectedKeyKind,
  EntitySuggestion,
  extractPrimaryValue,
  isSearchType,
  SearchType,
} from '../../interfaces/IAffectedEntitySearch'

export interface SearchCriteria {
  searchType: SearchType
  searchTerm: string
  fromDate?: Date | null
  toDate?: Date | null
  isOnlyActive: boolean
}

export type FilterOptions = 'daily' | 'incident' | 'planned' | 'generic' | 'all' | 'search'

const FILTER_OPTIONS: FilterOptions[] = ['daily', 'incident', 'planned', 'generic', 'all', 'search']

function isFilterOption(value: string | null | undefined): value is FilterOptions {
  return !!value && (FILTER_OPTIONS as string[]).includes(value)
}

type InitialContextValue = {
  filteredEvents: EventSnippet[]
  searchedEvents: EventSnippet[]
  setSearchedEvents: Dispatch<SetStateAction<EventSnippet[]>>
  setFilteredEvents: Dispatch<SetStateAction<EventSnippet[]>>
  getDailyEvents: () => Promise<void>
  handleFilterChange: (filter: string) => Promise<void>
  searchCriteria: SearchCriteria
  executedSearchCriteria: SearchCriteria | null
  selectedFilter: FilterOptions
  isSearchBarCollapsed: boolean
  setIsSearchBarCollapsed: Dispatch<SetStateAction<boolean>>
  setSelectedFilter: Dispatch<SetStateAction<FilterOptions>>
  setSearchCriteria: Dispatch<SetStateAction<SearchCriteria>>
  showSearchFilterButton: boolean
  setShowSearchFilterButton: Dispatch<SetStateAction<boolean>>
  loadingEvents: boolean
  setLoadingEvents: Dispatch<SetStateAction<boolean>>
  runSearch: (criteria: SearchCriteria) => Promise<void>
  fetchEntitySuggestions: (kind: AffectedKeyKind, query: string) => Promise<EntitySuggestions>
}

export interface EntitySuggestions {
  values: EntitySuggestion[]
  hasMore: boolean
}

export const EventsContext = createContext({} as InitialContextValue)

interface EventsProviderProps {
  children: ReactNode
}

export const EMPTY_SEARCH_CRITERIA: SearchCriteria = {
  searchType: 'FreeText',
  searchTerm: '',
  fromDate: null,
  toDate: null,
  isOnlyActive: false,
}

function EventsProvider({ children }: EventsProviderProps) {
  const [filteredEvents, setFilteredEvents] = useState<EventSnippet[]>([])
  const [showSearchFilterButton, setShowSearchFilterButton] = useState<boolean>(false)
  const [searchedEvents, setSearchedEvents] = useState<EventSnippet[]>([])
  const [isSearchBarCollapsed, setIsSearchBarCollapsed] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<FilterOptions>('daily')
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(EMPTY_SEARCH_CRITERIA)
  const [executedSearchCriteria, setExecutedSearchCriteria] = useState<SearchCriteria | null>(null)

  const { context } = useContext(EbAuthenticatedAppContext)
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const hasHydratedRef = useRef(false)

  const runSearch = async (criteria: SearchCriteria) => {
    if (!criteria.searchTerm.trim() && criteria.searchType !== 'FreeText') return

    setLoading(true)
    setShowSearchFilterButton(true)

    const trimmed = criteria.searchTerm.trim()

    let response
    if (criteria.searchType === 'EventId') {
      response = await context.restApiClient
        .buildRequest(`/api/events/search/by-id/${encodeURIComponent(trimmed)}`)
        .deserializeResponseToJson<EventSnippet[]>()
        .getAsync()
    } else if (criteria.searchType === 'FreeText') {
      response = await context.restApiClient
        .buildRequest('/api/events/search')
        .serializeToJsonContent({
          searchTerm: criteria.searchTerm,
          fromDate: criteria.fromDate,
          toDate: criteria.toDate,
          isOnlyActive: criteria.isOnlyActive,
        })
        .deserializeResponseToJson<EventSnippet[]>()
        .putAsync()
    } else {
      // Customer suggestions commit "12345 — Telia AS" to the input; the backend expects just
      // the canonical number, so strip the trailing " — name" portion before submitting.
      response = await context.restApiClient
        .buildRequest('/api/events/search/by-entity')
        .serializeToJsonContent({
          kind: criteria.searchType as AffectedKeyKind,
          value: extractPrimaryValue(trimmed),
          fromDate: criteria.fromDate,
          toDate: criteria.toDate,
          isOnlyActive: criteria.isOnlyActive,
        })
        .deserializeResponseToJson<EventSnippet[]>()
        .putAsync()
    }

    if (response.json) {
      setSearchedEvents(response.json)
      setFilteredEvents(response.json)
      setSelectedFilter('search')
      setExecutedSearchCriteria(criteria)
    }

    if (response.error) {
      setSearchedEvents([])
      setFilteredEvents([])
      enqueueSnackbar(<Typography variant="body2">Noe gikk galt. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    }

    setLoading(false)
  }

  const fetchEntitySuggestions = async (kind: AffectedKeyKind, query: string): Promise<EntitySuggestions> => {
    const trimmed = query.trim()
    if (trimmed.length < 3) return { values: [], hasMore: false }

    const params = new URLSearchParams({ kind, query: trimmed })
    const response = await context.restApiClient
      .buildRequest(`/api/events/search/suggest-entity?${params.toString()}`)
      .deserializeResponseToJson<EntitySuggestions>()
      .getAsync()

    return response.json ?? { values: [], hasMore: false }
  }

  const getDailyEvents = async () => {
    if (selectedFilter !== 'daily') return

    setLoading(true)

    const dailyEvents = await context.restApiClient.buildRequest('/api/events/daily').deserializeResponseToJson<EventSnippet[]>().getAsync()

    if (dailyEvents.json) {
      setFilteredEvents(dailyEvents.json)
    }

    if (dailyEvents.error) {
      setFilteredEvents([])
      enqueueSnackbar(<Typography variant="body2">Kunne ikke hente dagens hendelser. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    }

    return setLoading(false)
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleFilterChange = async (filter: string) => {
    setLoading(true)

    if (filter === 'search') {
      setSelectedFilter('search')
      setFilteredEvents(searchedEvents)
      // eslint-disable-next-line sonarjs/no-useless-react-setstate
      setSearchedEvents(searchedEvents)
    }

    if (filter === 'daily') {
      setSelectedFilter('daily')
      const dailyEvents = await context.restApiClient.buildRequest('/api/events/daily').deserializeResponseToJson<EventSnippet[]>().getAsync()

      if (dailyEvents.json) {
        setFilteredEvents(dailyEvents.json)
      }

      if (dailyEvents.error) {
        setFilteredEvents([])
        enqueueSnackbar(<Typography variant="body2">Kunne ikke hente dagens hendelser. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    }
    if (filter === 'generic') {
      setSelectedFilter('generic')
      const genericEvents = await context.restApiClient.buildRequest('/api/events/generic').deserializeResponseToJson<EventSnippet[]>().getAsync()

      if (genericEvents.json) {
        setFilteredEvents(genericEvents.json)
      }

      if (genericEvents.error) {
        setFilteredEvents([])
        enqueueSnackbar(<Typography variant="body2">Kunne ikke hente generelle hendelser. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    }
    if (filter === 'incident') {
      setSelectedFilter('incident')
      const incidentEvents = await context.restApiClient.buildRequest('/api/events/incident').deserializeResponseToJson<EventSnippet[]>().getAsync()

      if (incidentEvents.json) {
        setFilteredEvents(incidentEvents.json)
      }

      if (incidentEvents.error) {
        setFilteredEvents([])
        enqueueSnackbar(<Typography variant="body2">Kunne ikke hente fellesfeil hendelser. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    }
    if (filter === 'planned') {
      setSelectedFilter('planned')
      const plannedEvents = await context.restApiClient.buildRequest('/api/events/planned').deserializeResponseToJson<EventSnippet[]>().getAsync()

      if (plannedEvents.json) {
        setFilteredEvents(plannedEvents.json)
      }

      if (plannedEvents.error) {
        setFilteredEvents([])
        enqueueSnackbar(<Typography variant="body2">Kunne ikke hente planlagte hendelser. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    }
    if (filter === 'all') {
      setSelectedFilter('all')
      const response = await context.restApiClient.buildRequest('/api/events/all').deserializeResponseToJson<EventSnippet[]>().getAsync()

      if (response.json) {
        setFilteredEvents(response.json)
      }

      if (response.error) {
        enqueueSnackbar(<Typography variant="body2">Kunne ikke hente hendelser. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    }

    return setLoading(false)
  }

  useEffect(() => {
    const tab = searchParams.get('tab')
    const type = searchParams.get('type')
    const q = searchParams.get('q')

    const isInitialMount = !hasHydratedRef.current
    hasHydratedRef.current = true

    if (tab === 'search' && isSearchType(type) && q) {
      const stateMatches =
        selectedFilter === 'search' &&
        searchCriteria.searchType === type &&
        searchCriteria.searchTerm === q
      if (!isInitialMount && stateMatches) return
      const restored: SearchCriteria = { ...EMPTY_SEARCH_CRITERIA, searchType: type, searchTerm: q }
      setSearchCriteria(restored)
      runSearch(restored)
      return
    }

    if (isFilterOption(tab) && tab !== 'search') {
      if (!isInitialMount && selectedFilter === tab) return
      handleFilterChange(tab)
      return
    }

    if (tab === 'search') return

    if (isInitialMount) {
      getDailyEvents()
    } else if (selectedFilter !== 'daily') {
      handleFilterChange('daily')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    if (!hasHydratedRef.current) return

    const next = new URLSearchParams()
    if (selectedFilter !== 'daily') next.set('tab', selectedFilter)
    if (selectedFilter === 'search') {
      next.set('type', searchCriteria.searchType)
      if (searchCriteria.searchTerm) next.set('q', searchCriteria.searchTerm)
    }

    const urlTab = searchParams.get('tab') ?? 'daily'
    if (urlTab === selectedFilter) {
      const criticality = searchParams.get('criticality')
      const category = searchParams.get('category')
      if (criticality) next.set('criticality', criticality)
      if (category) next.set('category', category)
    }

    const nextStr = next.toString()
    if (nextStr !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, searchCriteria.searchType, searchCriteria.searchTerm])

  return (
    <EventsContext.Provider
      value={{
        filteredEvents: filteredEvents,
        setFilteredEvents: setFilteredEvents,
        searchedEvents: searchedEvents,
        setSearchedEvents: setSearchedEvents,
        getDailyEvents: getDailyEvents,
        handleFilterChange: handleFilterChange,
        searchCriteria: searchCriteria,
        executedSearchCriteria: executedSearchCriteria,
        selectedFilter: selectedFilter,
        isSearchBarCollapsed: isSearchBarCollapsed,
        setShowSearchFilterButton,
        showSearchFilterButton: showSearchFilterButton,
        setIsSearchBarCollapsed: setIsSearchBarCollapsed,
        setSelectedFilter: setSelectedFilter,
        setSearchCriteria: setSearchCriteria,
        loadingEvents: loading,
        setLoadingEvents: setLoading,
        runSearch: runSearch,
        fetchEntitySuggestions: fetchEntitySuggestions,
      }}
    >
      {children}
    </EventsContext.Provider>
  )
}

export default EventsProvider

export type { AffectedKeyKind, SearchType }
